import type { SnsWebSdkBaseConfig } from '@sumsub/websdk';

const DEFAULT_ISSUING_COUNTRY = 'AUS';

function issuingCountryFromEnv(): string {
  const v = process.env.REACT_APP_SUMSUB_ISSUING_COUNTRY?.trim();
  return v && v.length > 0 ? v : DEFAULT_ISSUING_COUNTRY;
}

function customizationNameFromEnv(): string | undefined {
  const v = process.env.REACT_APP_SUMSUB_CUSTOMIZATION_NAME?.trim();
  return v && v.length > 0 ? v : undefined;
}

export type BuildSumsubWebSdkConfInput = Pick<SnsWebSdkBaseConfig, 'lang' | 'theme'> & {
  /** Alpha-3 code; e.g. test page `?issuingCountry=GBR` */
  issuingCountryOverride?: string | null;
  /** Overrides `REACT_APP_SUMSUB_CUSTOMIZATION_NAME`; e.g. test page `?customizationName=...` */
  customizationNameOverride?: string | null;
};

function customizationNameResolved(input: BuildSumsubWebSdkConfInput): string | undefined {
  const fromInput = input.customizationNameOverride?.trim();
  if (fromInput && fromInput.length > 0) {
    return fromInput;
  }
  return customizationNameFromEnv();
}

/**
 * Shared WebSDK `withConf` payload: prefill issuing country (default Australia), optional Dashboard customization override.
 * Does not set documentDefinitions / autoSelectDocumentDefinitions so applicants still choose document type.
 *
 * The “Verification for …” phishing-warning interstitial is not controlled here. In Sumsub: Customizations →
 * WebSDK customization → General → enable “Skip warning screen”, attach that customization to the verification level,
 * enable WebSDK 2.0 on the level, then set `REACT_APP_SUMSUB_CUSTOMIZATION_NAME` (or pass `customizationNameOverride`)
 * to that customization’s exact name so this session uses it.
 */
export function buildSumsubWebSdkConf(input: BuildSumsubWebSdkConfInput): SnsWebSdkBaseConfig {
  const override = input.issuingCountryOverride?.trim();
  const country = override && override.length > 0 ? override : issuingCountryFromEnv();
  const customizationName = customizationNameResolved(input);

  const conf: SnsWebSdkBaseConfig = {
    lang: input.lang,
    theme: input.theme,
    country,
  };
  if (customizationName) {
    conf.customizationName = customizationName;
  }
  return conf;
}
